import context from '@truckdown/components-systems';
import { IAccountManager, UpdateAccountInfoModel, AccountModel } from "@truckdown/accounts";
import { defineComponent, ref, Ref, computed } from "vue";
import { useVuelidate, Validation } from '@vuelidate/core';
import { showMessage } from "../../Utilities/Utilities";
import { required, email, url } from '@vuelidate/validators';
import validationError from "../../Validation/Error/Error.vue";

const phone = (value: string) => value == "" || /^\+?(1|\d{3})?[-. ]?\(?(\d{3})\)?[-. ]?(\d{3})[-. ]?(\d{4})$/.test(value)

interface IData {
    isLoading: Ref<boolean>,
    account: Ref<AccountModel>,
    update: Ref<IFormData>
};

interface IFormData {
    accountId: string,
    name: string,
    email: string,
    officeNumber: string,
    faxNumber: string,
    website: string,
}

const validations = {
    accountId: {
        required
    },
    name: {
        required,
        $autoDirty: true
    },
    email: {
        email,
        $autoDirty: true
    },
    officeNumber: {
        phone,
        required,
        $autoDirty: true
    },
    faxNumber: {
        phone,
        $autoDirty: true
    },
    website: {
        url,
        $autoDirty: true
    }
}

const getInitialData = function (props: any) {

    var update = {
        accountId: props.account.id,
        name: props.account.n,
        email: props.account.info.email,
        officeNumber: props.account.info.officeNumber,
        faxNumber: props.account.info.faxNumber,
        website: props.account.info.website
    } as IFormData;

    return {
        isLoading: ref<boolean>(false),
        account: ref<AccountModel>(props.account),
        update: ref<IFormData>(update)
    } as IData;
}

const getMethods = function (props: any, data: IData, ctx: any, validator: Ref<Validation>) {

    const validate = function () {
        validator.value.$touch();
        return validator.value.$validate()
            .then((res) => {
                return res;
            });
    }

    return {
        cancelClick: function (): void {
            ctx.emit(cancelEventName);
        },
        saveClick: async function (): Promise<void> {
            validate().then(async (res) => {
                if (res) {
                    await save(data, ctx);
                }
            });
        }
    }
}

const save = async function (data: IData, ctx: any): Promise<void> {
    if (!data.isLoading.value) {
        data.isLoading.value = true;

        try {
            let accountManager = await context.getService<IAccountManager>('IAccountManager');

            if (data.update.value.website &&
                data.update.value.website != null &&
                data.update.value.website.length > 0) {
                if (!/^https?:\/\//i.test(data.update.value.website)) {
                    data.update.value.website = 'https://' + data.update.value.website;
                }
            }

            var response = await accountManager.updateInfo(data.update.value);

            if (!response || !response.ok) {
                console.log("Error updating company information: " + response.message);
                showMessage("Error updating company information.", "danger", true, true);
            } else {
                showMessage("Company information was succssfully updated.", "success", true, true);

                if (data.account.value.id == context.user?.accountId) {
                    let returnUrl = window.location.pathname + window.location.search + window.location.hash;
                    window.location.href = '/systems/context/refresh?returnUrl=' + encodeURIComponent(returnUrl);
                } else {
                    ctx.emit(updatedEventName, response.data);
                }
            }
        } catch (err) {
            console.error(err);
        } finally {
            data.isLoading.value = false;
        }
    }
}

const updatedEventName = 'update';
const cancelEventName = 'cancel';

export default defineComponent({
    props: {
        account: {
            type: Object as () => AccountModel,
            required: true
        }
    },
    components: {
        'error': validationError,
    },
    emits: [updatedEventName, cancelEventName],
    setup: function (props, ctx) {
        let data = getInitialData(props);

        const v$ = useVuelidate(validations, data.update.value);

        let methods = getMethods(props, data, ctx, v$);

        return {
            ...data,
            ...computed,
            ...methods,
            v$
        };
    }
});